// PersonaPanel.jsx — formulario de donación Persona: Transferencia / Tarjeta.

import React from 'react'
import { fmtCLP } from '../shared.jsx'
import { PRESETS, useAmountPicker, AmountPicker } from './AmountPicker.jsx'
import { BankInfoBlock } from './BankInfoBlock.jsx'
import { BankTransferNote } from './BankTransferNote.jsx'
import { MercadoPagoNote } from './MercadoPagoNote.jsx'
import { ReceiptUploadNote } from './ReceiptUploadNote.jsx'
import { ReceiptDropzone } from './ReceiptDropzone.jsx'
import { SuccessState } from './SuccessState.jsx'
import { PaymentMethodToggle } from './PaymentMethodToggle.jsx'
import { CardPaymentSummary } from './CardPaymentSummary.jsx'
import { cardDonateButtonLabel } from './processingFee.js'
import { scrollToComo } from './scrollToComo.js'
import { isValidEmail } from './validators.js'
import { trackCta } from '../../lib/analytics.js'

const { useState } = React;

function PersonaPanel({ onDonate, mercadopagoFeeRate = 0 }) {
  const [method, setMethod] = useState("transfer");
  const { amount, custom, finalAmount, selectAmount, setCustom, reset: resetAmount } = useAmountPicker(25000);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [coverProcessingFee, setCoverProcessingFee] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmedAmount, setConfirmedAmount] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState({});

  function markTouched(field) {
    setTouched((t) => (t[field] ? t : { ...t, [field]: true }));
  }

  const nameValid = !!name;
  const emailValid = !!email && isValidEmail(email);

  const requiredFields = [
    { id: "persona-name", label: "tu nombre", valid: nameValid },
    { id: "persona-email", label: "un correo electrónico válido", valid: emailValid },
  ];
  const missingFields = requiredFields.filter((f) => !f.valid).map((f) => f.label);
  if (finalAmount < 1000) missingFields.push("un monto de al menos $1.000");
  const canSubmit = missingFields.length === 0 && !submitting;

  function fieldClass(isFieldInvalid) {
    return `field ${isFieldInvalid ? "field-invalid" : ""}`;
  }

  const showEmailError = (attempted || touched.email) && email && !isValidEmail(email);

  function handleSubmitClick() {
    trackCta("donate_submit", { kind: "persona", payment_method: method });
    if (!canSubmit) {
      setAttempted(true);
      const firstMissing = requiredFields.find((f) => !f.valid);
      const targetId = firstMissing ? firstMissing.id : "persona-amount";
      const el = document.getElementById(targetId);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus?.();
      return;
    }
    submit();
  }

  function switchMethod(next) {
    setMethod(next);
    setError(null);
    if (next === "card") setCoverProcessingFee(true);
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await onDonate({
        kind: "person",
        amount: finalAmount,
        paymentMethod: method,
        name,
        email,
        anonymous,
        receiptReference: method === "transfer" ? receiptReference : "",
        receiptFile: method === "transfer" ? receiptFile : null,
        coverProcessingFee: method === "card" ? coverProcessingFee : false,
      });
      if (method === "transfer") {
        setConfirmedAmount(finalAmount);
        scrollToComo();
      }
    } catch (e) {
      setError(e?.message || "No pudimos procesar tu aporte. Intenta nuevamente.");
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setConfirmedAmount(null);
    resetAmount();
    setName("");
    setEmail("");
    setAnonymous(false);
    setReceiptReference("");
    setReceiptFile(null);
    setCoverProcessingFee(true);
    setError(null);
    setAttempted(false);
    setTouched({});
  }

  if (confirmedAmount !== null) {
    return <SuccessState amount={confirmedAmount} method={method} onReset={reset} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PaymentMethodToggle
        method={method}
        onChange={switchMethod}
        transferLabel="Transferencia Bancaria"
        cardLabel="Tarjeta / Digital"
      />

      {method === "transfer" && <BankTransferNote />}

      {method === "transfer" && <BankInfoBlock />}

      {method === "transfer" && (
        <div className="notice-success">
          <strong>Sin comisiones</strong> — el 100% de tu aporte llega directo al Cuerpo de Bomberos, sin descuentos.
        </div>
      )}

      {method === "card" && <MercadoPagoNote />}

      <div id="persona-amount">
        <label className="kicker" style={{ marginBottom: 10, display: "block" }}>
          {method === "transfer" ? "¿Cuánto vas a transferir?" : "Elige un monto"}
        </label>
        <AmountPicker
          presets={PRESETS}
          amount={amount}
          custom={custom}
          finalAmount={finalAmount}
          onSelect={selectAmount}
          onCustomChange={setCustom}
        />
      </div>

      <input
        id="persona-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Tu nombre *"
        className={fieldClass(attempted && !nameValid)}
      />
      <div>
        <input
          id="persona-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => markTouched("email")}
          placeholder={
            method === "transfer"
              ? "tu@correo.cl — para recibir boleta de honor *"
              : "tu@correo.cl — para el correo de agradecimiento *"
          }
          className={fieldClass((attempted && !emailValid) || showEmailError)}
        />
        {showEmailError && <span className="field-error-text">Correo inválido</span>}
      </div>
      <label className="consent-row">
        <input
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
        />
        <span>
          Donación anónima (tu nombre no será publicado, esto es solo para poder registrar tu donación).
        </span>
      </label>

      {method === "transfer" && <ReceiptUploadNote />}

      {method === "transfer" && (
        <div>
          <label className="kicker" style={{ marginBottom: 10, display: "block" }}>
            Comprobante de transferencia (opcional)
          </label>
          <ReceiptDropzone
            reference={receiptReference}
            onReferenceChange={setReceiptReference}
            file={receiptFile}
            onFileChange={setReceiptFile}
          />
        </div>
      )}

      {attempted && missingFields.length > 0 && (
        <div className="notice-error" role="alert">
          Para donar, falta completar: {missingFields.join(", ")}.
        </div>
      )}

      {error && <div className="notice-error" role="alert">{error}</div>}

      {method === "card" && (
        <CardPaymentSummary
          netAmount={finalAmount}
          coverProcessingFee={coverProcessingFee}
          onCoverProcessingFeeChange={setCoverProcessingFee}
          feeRate={mercadopagoFeeRate}
        />
      )}

      <button
        className={`btn btn-primary btn-lg ${!canSubmit && !submitting ? "btn-pending" : ""}`}
        type="button"
        onClick={handleSubmitClick}
        disabled={submitting}
      >
        {submitting ? "Procesando…" : method === "transfer"
          ? `Registrar aporte · $${fmtCLP(finalAmount)}`
          : cardDonateButtonLabel({
              netAmount: finalAmount,
              coverProcessingFee,
              feeRate: mercadopagoFeeRate,
              submitting,
            })}
      </button>

      {method === "card" && (
        <div className="notice-info">
          <strong>Nota:</strong> El procesamiento de pago se realiza de forma segura. Los datos de tu tarjeta nunca son almacenados en nuestros servidores.
        </div>
      )}
    </div>
  );
}

export { PersonaPanel };
